import React, { useState, useEffect, useMemo } from "react";
import QuestionImage from "./components/QuestionImage";
import { useParams, useNavigate } from "react-router-dom";
import { auth, db } from "./firebaseConfig";
import {
  collection,
  query,
  where,
  getDocs,
  doc,
  getDoc,
  documentId,
} from "firebase/firestore";
import { exportQuizToWord } from "./utils/wordExportService";
import {
  buildAssessmentSeries,
  finalPolicyLabel,
  outcomeDisplayLabel,
} from "./utils/attemptResults";
import {
  isMultipleChoiceQuestion,
  resolvedCorrectOptionForTeacher,
} from "./utils/quizQuestionCompatibility";
import {
  ArrowLeft,
  Award,
  BarChart3,
  BookOpenCheck,
  ChevronDown,
  Download,
  FileQuestion,
  Gauge,
  Search,
  Target,
  TrendingUp,
  UserRound,
  UsersRound,
} from "lucide-react";
import { accountDisplayName, DashboardHeader, DashboardSidebar } from "./Dashboard";
import "./QuizResults.css";
import { API_BASE_URL } from "./utils/apiBase";
import { WorkTypeFilter, WorkTypeTag } from "./components/WorkType";
import { averagesByWorkType, matchesWorkType } from "./utils/workType";

// The page's existing average: a plain mean of the student percentages.
const meanOf = (values) => values.length
  ? values.reduce((total, value) => total + value, 0) / values.length
  : null;

const compactResultMethodLabel = (policy) =>
  policy === "average" ? "Average" : finalPolicyLabel(policy);

const QuizResults = ({ user }) => {
  // Route param is now the Firestore quiz document ID (or legacy quizCode for backward compat)
  const { quizId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quizData, setQuizData] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [assessmentSeries, setAssessmentSeries] = useState([]);
  // Each student's series type from the server (null when unclear).
  const [workTypes, setWorkTypes] = useState(() => new Map());
  const [workFilter, setWorkFilter] = useState("all");
  const [users, setUsers] = useState({});
  const [questions, setQuestions] = useState({});
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [isExportingWord, setIsExportingWord] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [darkMode, setDarkMode] = useState(
    () => localStorage.getItem("darkMode") === "true"
  );
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [copiedTeacherCode, setCopiedTeacherCode] = useState(false);

  useEffect(() => {
    document.body.classList.toggle("dark-mode", darkMode);
    document.documentElement.setAttribute(
      "data-dark-mode",
      darkMode ? "true" : "false"
    );
    localStorage.setItem("darkMode", JSON.stringify(darkMode));
  }, [darkMode]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === "darkMode") setDarkMode(event.newValue === "true");
    };
    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  // Toggle question expansion to show/hide mark scheme
  const toggleQuestionExpansion = (questionId) => {
    setExpandedQuestions((prev) => ({
      ...prev,
      [questionId]: !prev[questionId],
    }));
  };

  // ── Fetch all data in parallel ──────────────────────────────────
  useEffect(() => {
    const fetchAllData = async () => {
      try {
        setLoading(true);

        // ── Debug: entry point ──
        console.log("[QUIZ RESULTS] Route param:", quizId);
        console.log("[QUIZ RESULTS] Current user UID:", user?.uid);
        console.log("[QUIZ RESULTS] Current user role:", user?.role);

        if (!user) {
          setError("You must be logged in to view test results.");
          setLoading(false);
          return;
        }

        // ── Step 1: Try loading quiz by Firestore document ID (preferred) ──
        let quiz = null;
        let usingLegacyFallback = false;

        const quizDocRef = doc(db, "quizzes", quizId);
        const quizDocSnap = await getDoc(quizDocRef);

        if (quizDocSnap.exists()) {
          // Primary path: found by document ID
          quiz = { id: quizDocSnap.id, ...quizDocSnap.data() };
          console.log("[QUIZ RESULTS] Loaded quiz ID:", quiz.id);
          console.log("[QUIZ RESULTS] Using legacy quizCode fallback: false");
        } else {
          // ── Step 1b: Legacy fallback — treat param as a quizCode ──
          // IMPORTANT: scope to the current user's quizzes only, never globally.
          usingLegacyFallback = true;
          console.log("[QUIZ RESULTS] Using legacy quizCode fallback: true");

          const quizzesRef = collection(db, "quizzes");

          if (user.role === "admin") {
            // Admins may query by quizCode without ownership restriction
            const legacyQuery = query(
              quizzesRef,
              where("quizCode", "==", quizId)
            );
            const legacySnap = await getDocs(legacyQuery);
            if (!legacySnap.empty) {
              const legacyDoc = legacySnap.docs[0];
              quiz = { id: legacyDoc.id, ...legacyDoc.data() };
              console.log(
                "[QUIZ RESULTS] Loaded quiz ID (legacy admin):",
                quiz.id
              );
            }
          } else {
            // Teachers: quizCode match AND owned by this teacher only
            const legacyQuery = query(
              quizzesRef,
              where("quizCode", "==", quizId),
              where("creatorUid", "==", user.uid)
            );
            const legacySnap = await getDocs(legacyQuery);

            if (legacySnap.size > 1) {
              setError(
                "Multiple tests with this code exist under your account. " +
                  "Please open the test directly from My Tests to view results."
              );
              setLoading(false);
              return;
            }

            if (!legacySnap.empty) {
              const legacyDoc = legacySnap.docs[0];
              quiz = { id: legacyDoc.id, ...legacyDoc.data() };
              console.log(
                "[QUIZ RESULTS] Loaded quiz ID (legacy teacher):",
                quiz.id
              );
            }
          }
        }

        if (!quiz) {
          setError(`No test found with identifier: ${quizId}`);
          setLoading(false);
          return;
        }

        // ── Step 2: Ownership verification ──────────────────────────────
        const ownerUid = quiz.creatorUid;
        console.log("[QUIZ RESULTS] Quiz owner UID:", ownerUid);

        const isOwner =
          user.role === "admin" || quiz.creatorUid === user.uid;
        console.log("[QUIZ RESULTS] Ownership check passed:", isOwner);

        if (!isOwner) {
          setError(
            "You do not have permission to view results for this test."
          );
          setLoading(false);
          return;
        }

        setQuizData(quiz);

        // Load presentation-safe results through the authenticated server.
        // MCQ option IDs are resolved against authoritative question documents
        // before any submission result reaches this browser view.
        const currentUser = auth.currentUser;
        if (!currentUser) throw new Error("You must be signed in to view test results.");
        const idToken = await currentUser.getIdToken();
        const response = await fetch(
          `${API_BASE_URL}/api/quiz-responses?quizId=${encodeURIComponent(quiz.id)}`,
          { headers: { Authorization: `Bearer ${idToken}` } }
        );
        const responseData = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(responseData.error || "Test responses could not be loaded.");
        const allResponses = Array.isArray(responseData.responses) ? responseData.responses : [];
        const builtSeries = buildAssessmentSeries({
          records: allResponses,
          attempts: Array.isArray(responseData.attempts) ? responseData.attempts : [],
          finalResults: Array.isArray(responseData.finalResults) ? responseData.finalResults : [],
          defaultQuizId: quiz.id,
        });
        const scopedSubmissions = builtSeries.flatMap((series) =>
          series.attempts.find(({ id }) => id === series.selectedAttemptId)?.records || []
        );
        setAssessmentSeries(builtSeries);
        setWorkTypes(new Map((Array.isArray(responseData.seriesWorkTypes) ? responseData.seriesWorkTypes : [])
          .map(({ studentUid, workType }) => [studentUid, workType === "homework" || workType === "assessment" ? workType : null])));
        console.log("[QUIZ RESULTS] Scoped submissions shown:", scopedSubmissions.length);

        // No scoped submissions — still load questions for the quiz display
        if (scopedSubmissions.length === 0) {
          if (quiz.questionIds && quiz.questionIds.length > 0) {
            const questionsData = await fetchQuestions(quiz.questionIds);
            setQuestions(questionsData);
          }
          setLoading(false);
          return;
        }

        setSubmissions(scopedSubmissions);

        // ── Step 5: Extract unique user IDs and question IDs ──────────────
        const questionIdSet = new Set(quiz.questionIds || []);
        const usersData = {};

        scopedSubmissions.forEach((submission) => {
          if (submission.userId) {
            usersData[submission.userId] = {
              username: submission.username || "Unknown",
              displayName: submission.username || "Unknown",
              email: submission.email || "No email",
            };
          }
          if (submission.questionId) {
            const questionId = submission.questionId.includes("/")
              ? submission.questionId.split("/").pop()
              : submission.questionId;
            questionIdSet.add(questionId);
          }
        });

        // ── Step 6: Fetch authoritative questions for analytics/export ─────
        const questionsData = await fetchQuestions(Array.from(questionIdSet));

        setUsers(usersData);
        setQuestions(questionsData);
        setLoading(false);
      } catch (err) {
        console.error(
          "Error fetching quiz results — code:",
          err.code,
          "| message:",
          err.message,
          err
        );
        if (err.code === "permission-denied") {
          setError(
            "You don't have permission to view submissions. Please contact an administrator."
          );
        } else {
          setError("Failed to load test results. Please try again.");
        }
        setLoading(false);
      }
    };

    const fetchQuestions = async (questionIds) => {
      if (!questionIds || questionIds.length === 0) return {};
      try {
        const questionMap = {};
        for (let i = 0; i < questionIds.length; i += 10) {
          const batch = questionIds.slice(i, i + 10);
          const questionsRef = collection(db, "questions");
          const questionsQuery = query(
            questionsRef,
            where(documentId(), "in", batch)
          );
          const questionsSnapshot = await getDocs(questionsQuery);
          questionsSnapshot.docs.forEach((d) => {
            questionMap[d.id] = { id: d.id, ...d.data() };
          });
        }
        return questionMap;
      } catch (err) {
        console.error("Error fetching questions:", err);
        return {};
      }
    };

    fetchAllData();
  }, [quizId, user]);

  // ── Compute student data ────────────────────────────────────────
  const students = useMemo(() => {
    if (
      !submissions.length ||
      !Object.keys(users).length ||
      !Object.keys(questions).length
    ) {
      return [];
    }

    const submissionsByUser = {};
    submissions.forEach((submission) => {
      const userId = submission.userId;
      if (!userId) return;
      if (!submissionsByUser[userId]) submissionsByUser[userId] = [];
      submissionsByUser[userId].push(submission);
    });

    return Object.keys(submissionsByUser).map((userId) => {
      const userData = users[userId] || {};
      const userSubmissions = submissionsByUser[userId];
      let totalScore = 0;
      let totalMaxScore = 0;

      userSubmissions.forEach((submission) => {
        const markAwarded = parseFloat(submission.markAwarded) || 0;
        totalScore += markAwarded;
        if (submission.questionId) {
          const questionId = submission.questionId.includes("/")
            ? submission.questionId.split("/").pop()
            : submission.questionId;
          const questionData = questions[questionId];
          if (questionData) {
            totalMaxScore += parseFloat(questionData.maxMark) || 0;
          }
        }
      });

      const series = assessmentSeries.find((item) => item.studentUid === userId);
      const canonical = series?.finalResult || null;
      const summary = canonical || series?.summary || null;
      return {
        id: userId,
        username: userData.username || userData.displayName || "Unknown",
        email: userData.email || "No email",
        score: summary?.totalAwarded ?? totalScore,
        maxScore: summary?.totalPossible ?? totalMaxScore,
        percentage: Number.isFinite(summary?.percentage)
          ? Number(summary.percentage).toFixed(2)
          : totalMaxScore > 0 ? ((totalScore / totalMaxScore) * 100).toFixed(2) : 0,
        finalResult: canonical,
        attemptCount: series?.attempts.length || 1,
        outcome: outcomeDisplayLabel(canonical?.achievedOutcomeLabel) || null,
        resultMethod: canonical ? compactResultMethodLabel(canonical.policy) : "Score only",
        status: series?.displayStatus || "completed",
        selectedAttemptId: series?.selectedAttemptId || null,
        submissions: userSubmissions,
        workType: workTypes.has(userId) ? workTypes.get(userId) : "assessment",
      };
    });
  }, [submissions, users, questions, assessmentSeries, workTypes]);

  // The work filter scopes the student list, the summary and the question
  // analysis together.
  const typedStudents = useMemo(
    () => students.filter((student) => matchesWorkType(student.workType, workFilter)),
    [students, workFilter]
  );
  const typedSubmissions = useMemo(() => {
    if (workFilter === "all") return submissions;
    const ids = new Set(typedStudents.map(({ id }) => id));
    return submissions.filter((submission) => ids.has(submission.userId));
  }, [submissions, typedStudents, workFilter]);

  // ── Compute question statistics (scoped to this quiz's questionIds) ─
  const questionStats = useMemo(() => {
    if (
      !typedSubmissions.length ||
      !Object.keys(questions).length ||
      !quizData?.questionIds
    ) {
      return [];
    }

    // Only analyse questions belonging to THIS quiz
    const questionStatsMap = {};
    quizData.questionIds.forEach((qId) => {
      const question = questions[qId] || {};
      questionStatsMap[qId] = {
        id: qId,
        totalAttempts: 0,
        markedCount: 0,
        totalScore: 0,
        totalPossibleScore: 0,
        averagePercentage: 0,
        notAnsweredPercentage: 0,
        notAnsweredCount: 0,
        questionText: question.questionText || "Image-based question",
        maxMark: parseFloat(question.maxMark) || 0,
        questionImageURL: question.questionImageURL || "",
        ...(question.questionMedia ? { questionMedia: question.questionMedia } : {}),
      };
    });

    // submissions is already scoped — no further filtering needed
    typedSubmissions.forEach((submission) => {
      if (!submission.questionId) return;
      const questionId = submission.questionId.includes("/")
        ? submission.questionId.split("/").pop()
        : submission.questionId;
      if (!questionStatsMap[questionId]) return;

      const isNotAnswered =
        submission.userAnswer === "Question not answered by student" ||
        submission.userAnswer === "Not answered by student" ||
        (submission.questionType === "multipleChoice" &&
          submission.mcqResult &&
          !submission.mcqResult.selectedOption &&
          !submission.mcqResult.selectedAnswerUnavailable);

      questionStatsMap[questionId].totalAttempts++;

      if (isNotAnswered) {
        questionStatsMap[questionId].notAnsweredCount++;
        return;
      }

      // Only real marks count. "Pending" and "N/A" (not marked, e.g. a
      // drawing that could not be loaded) are not zeros.
      const numericMark = Number(submission.markAwarded);
      if (
        submission.markAwarded != null &&
        submission.markAwarded !== "" &&
        Number.isFinite(numericMark)
      ) {
        const mark = numericMark;
        const maxMark = questionStatsMap[questionId].maxMark;
        questionStatsMap[questionId].totalScore += mark;
        questionStatsMap[questionId].totalPossibleScore += maxMark;
        questionStatsMap[questionId].markedCount++;
      }
    });

    const statsArray = Object.values(questionStatsMap).map((stat) => {
      if (stat.totalPossibleScore > 0) {
        stat.averagePercentage =
          (stat.totalScore / stat.totalPossibleScore) * 100;
      }
      if (stat.totalAttempts > 0) {
        stat.notAnsweredPercentage =
          (stat.notAnsweredCount / stat.totalAttempts) * 100;
      }
      return stat;
    });

    return statsArray.sort(
      (a, b) => a.averagePercentage - b.averagePercentage
    );
  }, [typedSubmissions, questions, quizData]);

  // ── Get quiz questions for display ─────────────────────────────
  const quizQuestions = useMemo(() => {
    if (!quizData?.questionIds || !Object.keys(questions).length) return [];
    return quizData.questionIds.map((id) => questions[id]).filter((q) => q);
  }, [quizData, questions]);

  const sortedStudents = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    return [...typedStudents]
      .filter((student) => {
        if (!normalizedSearch) return true;
        return [student.username, student.email].some((value) =>
          String(value || "").toLowerCase().includes(normalizedSearch)
        );
      })
      .sort(
        (a, b) => parseFloat(b.percentage) - parseFloat(a.percentage)
      );
  }, [typedStudents, searchTerm]);

  const overviewMetrics = useMemo(() => {
    const percentages = typedStudents
      .map((student) => parseFloat(student.percentage))
      .filter(Number.isFinite);
    const average = meanOf(percentages);
    const highest = percentages.length ? Math.max(...percentages) : null;
    const categories = averagesByWorkType(
      students.map((student) => ({ workType: student.workType, percentage: parseFloat(student.percentage) }))
        .filter(({ percentage }) => Number.isFinite(percentage)),
      meanOf
    );

    return {
      students: typedStudents.length,
      average,
      highest,
      questions: quizQuestions.length,
      categories,
    };
  }, [students, typedStudents, quizQuestions]);
  const percentLabel = (value) => value == null ? "—" : value.toFixed(1) + "%";
  const averageTitle = workFilter === "homework" ? "Homework Average"
    : workFilter === "assessment" ? "Assessment Average" : "Average Score (all work)";

  // ── Helpers ────────────────────────────────────────────────────
  const getDifficultyClass = (pct) => {
    if (pct < 40) return "hard";
    if (pct < 70) return "moderate";
    return "easy";
  };
  const getDifficultyLabel = (pct) => {
    if (pct < 40) return "Difficult";
    if (pct < 70) return "Moderate";
    return "Easy";
  };
  const getPercentageClass = (pct) => {
    const n = parseFloat(pct);
    if (n >= 70) return "high";
    if (n >= 40) return "mid";
    return "low";
  };

  // ── Handle export quiz to Word ──────────────────────────────────
  const handleExportWord = async () => {
    if (!quizData || !quizQuestions || quizQuestions.length === 0) {
      alert("No test data available to export.");
      return;
    }
    setIsExportingWord(true);
    try {
      const result = await exportQuizToWord(
        {
          quizCode: quizData.quizCode,
          subject: quizData.subject,
          topic: quizData.topic,
          level: quizData.level,
          specCode: quizData.specCode,
        },
        quizQuestions
      );
      if (result.success) {
        alert(
          "Word documents generated successfully! Check your downloads folder."
        );
      } else {
        alert(`Failed to generate Word documents: ${result.error}`);
      }
    } catch (err) {
      console.error("Error exporting quiz to Word:", err);
      alert("Failed to export test. Please try again.");
    } finally {
      setIsExportingWord(false);
    }
  };

  const handleLogout = async () => {
    try {
      await auth.signOut();
      navigate("/");
    } catch (logoutError) {
      console.error("Error signing out", logoutError);
    }
  };

  const handleCopyTeacherCode = async () => {
    if (!user?.teacherCode) return;
    try {
      await navigator.clipboard.writeText(user.teacherCode);
      setCopiedTeacherCode(true);
      window.setTimeout(() => setCopiedTeacherCode(false), 1800);
    } catch (copyError) {
      console.error("Could not copy teacher code", copyError);
    }
  };

  const role = user?.role === "admin" ? "admin" : "teacher";
  const username = accountDisplayName(user, "StudyDen teacher");
  const quizTitle =
    quizData?.quizTitle || quizData?.title || quizData?.subject || "Test Results";
  const quizContext = [quizData?.subject, quizData?.topic, quizData?.level]
    .filter(Boolean)
    .join(" · ");

  const initialsFor = (value) =>
    String(value || "Student")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase())
      .join("") || "S";

  // ── Render ─────────────────────────────────────────────────────
  return (
    <div className={"sd-dashboard sd-brand-theme sd-role-" + role + " qr-page" + (darkMode ? " sd-dark" : "")}>
      <DashboardSidebar
        role={role}
        username={username}
        creditsRemaining={user?.creditsRemaining ?? null}
        teacherCode={user?.teacherCode || ""}
        onCopyCode={handleCopyTeacherCode}
        copied={copiedTeacherCode}
        onLogout={handleLogout}
        open={sidebarOpen}
        onClose={() => setSidebarOpen(false)}
        activePath="/view-quiz-responses"
        officialDashboardBrand
      />

      <div className="sd-workspace qr-workspace">
        <DashboardHeader
          username={username}
          role={role}
          darkMode={darkMode}
          onToggleTheme={() => setDarkMode((value) => !value)}
          onOpenMenu={() => setSidebarOpen(true)}
          search={searchTerm}
          onSearch={setSearchTerm}
        />

        <main className="sd-main qr-main">
          <section className="qr-hero">
            <div className="qr-hero-copy">
              <button type="button" className="qr-back-link" onClick={() => navigate("/view-quizzes")}>
                <ArrowLeft size={15} /> Back to My Tests
              </button>
              <p className="qr-eyebrow">Results &amp; Analytics</p>
              <h1>{loading ? "Test Results" : quizTitle}</h1>
              <p className="qr-hero-description">
                Review student performance and identify the questions that need the most attention.
              </p>
              {!loading && !error && quizData && (
                <div className="qr-hero-meta" aria-label="Test details">
                  <span><BookOpenCheck size={14} />{quizContext || "Test assessment"}</span>
                  <span><span className="qr-code-label">Test code</span><strong>{quizData.quizCode || quizId}</strong></span>
                </div>
              )}
            </div>

            <div className="qr-hero-side">
              {!loading && !error && quizQuestions.length > 0 && (
                <button type="button" className="qr-export-btn" onClick={handleExportWord} disabled={isExportingWord}>
                  <Download size={16} />{isExportingWord ? "Exporting…" : "Export Word"}
                </button>
              )}
              <div className="qr-hero-art" aria-hidden="true">
                <span className="qr-hero-icon"><BarChart3 size={40} /></span>
                <i className="qr-orbit qr-orbit-one"><TrendingUp size={18} /></i>
                <i className="qr-orbit qr-orbit-two"><Award size={18} /></i>
                <i className="qr-orbit qr-orbit-three"><Target size={18} /></i>
              </div>
            </div>
          </section>

          {loading ? (
            <section className="qr-state-panel" aria-live="polite">
              <div className="qr-loading-spinner" />
              <h2>Loading test analytics</h2>
              <p>Gathering student and question performance data…</p>
            </section>
          ) : error ? (
            <section className="qr-state-panel qr-error" role="alert">
              <span className="qr-state-icon"><BarChart3 size={24} /></span>
              <h2>Results unavailable</h2>
              <p>{error}</p>
              <button type="button" onClick={() => navigate("/view-quizzes")}>
                <ArrowLeft size={15} />Return to My Tests
              </button>
            </section>
          ) : (
            <>
              <WorkTypeFilter value={workFilter} onChange={setWorkFilter} className="qr-work-filter" />
              <section className="qr-metrics" aria-label="Test performance summary">
                <article className="qr-metric qr-metric-blue">
                  <span><UsersRound size={21} /></span>
                  <div><strong>{overviewMetrics.students}</strong><p>Students</p><small>Unique submissions</small></div>
                </article>
                <article className="qr-metric qr-metric-purple">
                  <span><Gauge size={21} /></span>
                  <div><strong>{percentLabel(overviewMetrics.average)}</strong><p>{averageTitle}</p><small>{workFilter === "all"
                    ? `Assessments ${percentLabel(overviewMetrics.categories.assessment)} · Homework ${percentLabel(overviewMetrics.categories.homework)}`
                    : "Across student totals"}</small></div>
                </article>
                <article className="qr-metric qr-metric-green">
                  <span><Award size={21} /></span>
                  <div><strong>{overviewMetrics.highest == null ? "—" : overviewMetrics.highest.toFixed(1) + "%"}</strong><p>Highest Score</p><small>Top student result</small></div>
                </article>
                <article className="qr-metric qr-metric-amber">
                  <span><FileQuestion size={21} /></span>
                  <div><strong>{overviewMetrics.questions}</strong><p>Questions</p><small>In this assessment</small></div>
                </article>
              </section>

              <section className="qr-panel qr-student-panel">
                <div className="qr-panel-heading">
                  <div className="qr-panel-title">
                    <span><UsersRound size={19} /></span>
                    <div><h2>Student Results</h2><p>Individual totals, ranked by overall percentage.</p></div>
                  </div>
                  <div className="qr-panel-tools">
                    <label className="qr-student-search">
                      <Search size={16} />
                      <input type="search" value={searchTerm} onChange={(event) => setSearchTerm(event.target.value)}
                        placeholder="Search students…" aria-label="Search students by name or email" />
                    </label>
                    <span className="qr-result-count">{sortedStudents.length} of {typedStudents.length}</span>
                  </div>
                </div>

                {typedStudents.length > 0 && sortedStudents.length > 0 ? (
                  <div className="qr-table-wrap">
                    <table className="qr-table">
                      <colgroup>
                        <col className="qr-col-student" />
                        <col className="qr-col-result" />
                        <col className="qr-col-outcome" />
                        <col className="qr-col-attempts" />
                        <col className="qr-col-method" />
                        <col className="qr-col-status" />
                        <col className="qr-col-action" />
                      </colgroup>
                      <thead><tr><th>Student</th><th>Assessment result</th><th>Outcome</th><th>Attempts</th><th>Result method</th><th>Status</th><th><span className="sr-only">Actions</span></th></tr></thead>
                      <tbody>
                        {sortedStudents.map((student) => {
                          const percentage = parseFloat(student.percentage) || 0;
                          const safePercentage = Math.min(100, Math.max(0, percentage));
                          return (
                            <tr key={student.id}>
                               <td data-label="Student">
                                 <div className="qr-student-identity">
                                   <span className="qr-student-avatar">{initialsFor(student.username)}</span>
                                   <span>
                                     <button type="button" className="qr-student-name-link" onClick={() => navigate(`/student-profile/${student.id}`)}>
                                       {student.username}
                                     </button>
                                     <small>{student.email}</small>
                                     <WorkTypeTag type={student.workType} className="qr-work-type" />
                                   </span>
                                 </div>
                               </td>
                              <td data-label="Assessment result">
                                <div className="qr-score-cell">
                                  <span className={"qr-percentage " + getPercentageClass(student.percentage)}>{student.percentage}%</span>
                                  {Number.isFinite(student.finalResult?.totalAwarded) && Number.isFinite(student.finalResult?.totalPossible) && <small>{student.finalResult.totalAwarded} / {student.finalResult.totalPossible}</small>}
                                  <span className="qr-score-track" aria-hidden="true"><i className={getPercentageClass(student.percentage)} style={{ width: safePercentage + "%" }} /></span>
                                </div>
                              </td>
                              <td data-label="Outcome">{student.outcome ? <span className="qr-outcome-chip">{student.outcome}</span> : "—"}</td>
                              <td data-label="Attempts">{student.attemptCount}</td>
                              <td data-label="Result method" className="qr-result-method-cell">{student.resultMethod}</td>
                              <td data-label="Status" className="qr-status-cell"><span className={`qr-status-chip is-${student.status}`}>{student.status === "in_progress" ? "In progress" : student.status === "marking" ? "Marking" : "Completed"}</span></td>
                              <td data-label="Action"><button type="button" className="qr-profile-link" onClick={() => navigate(`/view-quiz-responses?quizCode=${encodeURIComponent(quizData?.quizCode || "")}&studentId=${encodeURIComponent(student.id)}${student.selectedAttemptId ? `&attemptId=${encodeURIComponent(student.selectedAttemptId)}` : ""}`)}>Review attempts</button></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : typedStudents.length > 0 ? (
                  <div className="qr-empty"><span><Search size={22} /></span><div><h3>No matching students</h3><p>Try a different name or email address.</p></div></div>
                ) : students.length > 0 ? (
                  <div className="qr-empty"><span><UsersRound size={23} /></span><div><h3>No {workFilter === "homework" ? "homework" : "assessment"} results</h3><p>No students have results of this work type for this test.</p></div></div>
                ) : (
                  <div className="qr-empty"><span><UsersRound size={23} /></span><div><h3>No student results yet</h3><p>Completed test submissions will appear here automatically.</p></div></div>
                )}
              </section>

              <section className="qr-panel qr-analysis-panel">
                <div className="qr-panel-heading">
                  <div className="qr-panel-title"><span><BarChart3 size={19} /></span><div><h2>Question Analysis</h2><p>Questions are ordered from most difficult to easiest.</p></div></div>
                </div>

                {questionStats.length > 0 ? (
                  <div className="qr-analysis-list">
                    {questionStats.map((stat) => {
                      const diffClass = getDifficultyClass(stat.averagePercentage);
                      const questionNumber = (quizData?.questionIds || []).indexOf(stat.id) + 1;
                      const safeAverage = Math.min(100, Math.max(0, stat.averagePercentage || 0));
                      return (
                        <article key={stat.id} className="qr-analysis-card">
                          <div className="qr-analysis-top">
                            <span className="qr-question-number">Q{questionNumber || "—"}</span>
                            <div className="qr-analysis-question">
                              <h3>{stat.questionText}</h3>
                              <div className="qr-analysis-chips"><span>{stat.maxMark} marks</span><span>{stat.totalAttempts} attempts</span><span>{stat.notAnsweredCount} unanswered</span></div>
                            </div>
                            <span className={"qr-difficulty-badge " + diffClass}>{getDifficultyLabel(stat.averagePercentage)}</span>
                          </div>
                          {(stat.questionImageURL || stat.questionMedia) && <QuestionImage question={stat}
                            quizId={quizData.id || quizId} context={{ type: "owner" }}
                            alt="Question" className="qr-question-image" />}
                          <div className="qr-analysis-score">
                            <div><span>Average score</span><strong>{(stat.averagePercentage ?? 0).toFixed(1)}%</strong></div>
                            <span>{stat.markedCount > 0 ? (stat.totalScore / stat.markedCount).toFixed(1) : 0} / {stat.maxMark} points</span>
                          </div>
                          <div className="qr-bar-container" role="img" aria-label={"Average score " + safeAverage.toFixed(1) + " percent"}>
                            <div className={"qr-bar-fill " + diffClass} style={{ width: safeAverage + "%" }} />
                          </div>
                          <p className="qr-unanswered-note">{stat.notAnsweredCount} of {stat.totalAttempts} attempts were unanswered ({(stat.notAnsweredPercentage ?? 0).toFixed(1)}%).</p>
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="qr-empty"><span><BarChart3 size={23} /></span><div><h3>No question analytics yet</h3><p>Analytics will populate after marked submissions are available.</p></div></div>
                )}
              </section>

              <section className="qr-panel qr-questions-panel">
                <div className="qr-panel-heading">
                  <div className="qr-panel-title"><span><BookOpenCheck size={19} /></span><div><h2>Test Questions</h2><p>Open a question to review its example full-mark answer.</p></div></div>
                  <span className="qr-result-count">{quizQuestions.length} question{quizQuestions.length !== 1 ? "s" : ""}</span>
                </div>

                {quizQuestions.length > 0 ? (
                  <div className="qr-question-list">
                     {quizQuestions.map((question, index) => {
                       const isExpanded = expandedQuestions[question.id] || false;
                       const isMultipleChoice = isMultipleChoiceQuestion(question);
                       const correctOption = isMultipleChoice
                         ? resolvedCorrectOptionForTeacher(question)
                         : null;
                       return (
                        <article key={question.id} className={"qr-question-card" + (isExpanded ? " is-expanded" : "")}>
                          <button type="button" className="qr-question-toggle" onClick={() => toggleQuestionExpansion(question.id)}
                            aria-expanded={isExpanded} aria-controls={"qr-answer-" + question.id}>
                            <span className="qr-question-number">Q{index + 1}</span>
                            <span className="qr-question-summary">
                              <strong>{question.questionText || "Image-based question"}</strong>
                              <small>{question.maxMark || "N/A"} max mark</small>
                            </span>
                            <span className="qr-toggle-label">{isExpanded ? "Hide answer" : "View answer"}<ChevronDown size={17} /></span>
                          </button>
                          {(question.questionImageURL || question.questionMedia) && <QuestionImage question={question}
                            quizId={quizData.id || quizId} context={{ type: "owner" }}
                            alt="Question" className="qr-question-image qr-question-card-image" />}
                          {isExpanded && (
                            <div className="qr-answer-panel" id={"qr-answer-" + question.id}>
                              <span className="qr-answer-icon"><UserRound size={17} /></span>
                               <div>
                                 <strong>{isMultipleChoice ? "Correct answer" : "Example full-mark answer"}</strong>
                                 <div className={`qr-answer-content${isMultipleChoice ? " qr-mcq-answer" : ""}`}>
                                   {isMultipleChoice
                                     ? correctOption
                                       ? `${correctOption.label}. ${correctOption.text}`
                                       : "Review required — the authoritative correct option is unavailable."
                                     : question.studentModelAnswer || question.modelAnswer || question.markSchemeAnswer || "N/A"}
                                 </div>
                               </div>
                            </div>
                          )}
                        </article>
                      );
                    })}
                  </div>
                ) : (
                  <div className="qr-empty"><span><FileQuestion size={23} /></span><div><h3>No questions found</h3><p>This test does not currently have question data to display.</p></div></div>
                )}
              </section>
            </>
          )}
        </main>
      </div>
    </div>
  );
};

export default QuizResults;
